import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";
import { produce } from "immer";
import Axios from "axios";
import { flowRight } from "lodash";
import { Container, Content } from "rsuite";

import withLoader, { WithLoaderProps } from "hocs/withLoader/withLoader";
import withError, { WithErrorProps } from "hocs/withError/withError";

import useRefreshPage from "hooks/useRefreshPage";
import useRequestCancellation from "hooks/useRequestCancellation";

import {
    compareEntities,
    isSupported,
} from "utils/tenant-workflows/tenant-workflows-helper";
import constants from "utils/constants";

import { getErrorMessage } from "api/defaults";
import { loadDataImports } from "api/environments/tenants/data-import-api";
import { loadCustomerDetails } from "api/customers/customer-details-api";
import { loadEnrollCache } from "api/environments/tenants/tenant-enrollments-api";

import {
    CustomerType,
    ICustomerDetails,
} from "store/customers/customer-details/customer-details-types";
import {
    DataImport as DataImportEntity,
    FeatureState,
} from "store/environments/tenants/data-import/data-import-types";
import {
    EnrollCache,
    EnrollStep,
    EnrollWorkflow,
    StepStatus,
} from "store/environments/tenants/enroll-tenant/enroll-tenant-types";

import { AppDispatch, RootState } from "store/store";
import {
    getActiveStep,
    getEnrollCache,
    getEnrollWorkflow,
} from "store/environments/tenants/enroll-tenant/enroll-tenant-selectors";
import { getCustomerDetails } from "store/customers/customer-details/customer-details-selectors";
import {
    setActiveStepData,
    setEnrollCache,
} from "store/environments/tenants/enroll-tenant/enroll-tenant-actions";
import { setIsUnsavedChangeAvailable } from "store/common/unsaved-data/unsaved-data-actions";
import { setCustomerDetails } from "store/customers/customer-details/customer-details-actions";
import {
    getActiveEnvironmentId,
    getActiveEnvironmentMoniker,
} from "store/environments/environment-selectors";

import Table from "components/Environments/Tenants/LeftMenu/DataImport/Table/Table";

const useAppDispatch = () => useDispatch<AppDispatch>();

interface Props extends WithErrorProps, WithLoaderProps {
    setIsStepFormValid: Dispatch<SetStateAction<() => boolean>>;
}

const DataImport: React.FC<Props> = ({
    setIsStepFormValid,
    showLoader,
    showError,
}: Props) => {
    const cancelTokenSource = useRequestCancellation();

    const dispatch = useAppDispatch();

    const [dataFetched, setDataFetched] = useState<boolean>(false);

    const [entities, setEntities] = useImmer<DataImportEntity[]>([]);

    const activeEnvironmentMoniker = useSelector((state: RootState): string =>
        getActiveEnvironmentMoniker(state)
    );

    const activeEnvironmentId = useSelector((state: RootState): string =>
        getActiveEnvironmentId(state)
    );

    const customerDetails = useSelector(
        (state: RootState): ICustomerDetails | null => getCustomerDetails(state)
    );

    const enrollWorkflow = useSelector(
        (state: RootState): EnrollWorkflow | null => getEnrollWorkflow(state)
    );

    const activeStep = useSelector((state: RootState): EnrollStep | null =>
        getActiveStep(state)
    );

    const enrollCache = useSelector((state: RootState): EnrollCache | null =>
        getEnrollCache(state)
    );

    const isD365Customer = customerDetails?.type === CustomerType.D365;

    const disabled = activeStep?.status !== StepStatus.Running;

    const fetchData = () => {
        showLoader?.(true);
        showError?.("");

        Promise.all([fetchAxImports()])
            .then((value) => {
                if (value.some((v) => typeof v == "string")) {
                    const errors = new Set<string>(
                        value.filter((v) => typeof v == "string")
                    );

                    const message = [...errors].join(". ");
                    showError?.(message);
                }
            })
            .finally(() => {
                showLoader?.(false);
                setDataFetched(true);
            });
    };

    const fetchCustomerDetails = async (): Promise<void> => {
        if (customerDetails) {
            return;
        }

        showLoader?.(true);
        showError?.("");

        try {
            const [payload, status] = await loadCustomerDetails(
                enrollCache!.customerId
            );

            if (status === constants.statusCode.OK && payload) {
                dispatch(setCustomerDetails(payload));

                return;
            }

            showError?.(getErrorMessage(String(payload), status));
        } catch (e) {
            if (!Axios.isCancel(e)) {
                showError?.(String(e));

                return;
            }
        }

        showLoader?.(true);
    };

    const fetchCache = async (): Promise<void> => {
        if (enrollCache) {
            return;
        }

        showLoader?.(true);
        showError?.("");

        try {
            const [payload, status] = await loadEnrollCache(
                activeEnvironmentMoniker,
                enrollWorkflow!.id
            );

            if (status === constants.statusCode.OK && payload) {
                dispatch(setEnrollCache(payload));

                return;
            }

            showError?.(getErrorMessage(String(payload), status));
        } catch (e) {
            if (!Axios.isCancel(e)) {
                showError?.(String(e));

                return;
            }
        }

        showLoader?.(true);
    };

    const fetchAxImports = async (): Promise<string | null> => {
        try {
            switch (activeStep?.status) {
                case StepStatus.Running:
                    const [payload, status] = await loadDataImports(
                        activeEnvironmentId,
                        enrollCache!.tenantId,
                        cancelTokenSource
                    );
                    if (status === constants.statusCode.OK && payload) {
                        switch (customerDetails?.type) {
                            case CustomerType.D365:
                                const modifiedEntities = payload.data.map(
                                    (entity) =>
                                        isSupported(entity.cef)
                                            ? {
                                                  ...entity,
                                                  axImport: FeatureState.Off,
                                                  cef: FeatureState.On,
                                              }
                                            : entity
                                );
                                const sortedCefEntities =
                                    modifiedEntities.sort(compareEntities);

                                setEntities(sortedCefEntities);

                                dispatch(
                                    setActiveStepData(
                                        JSON.stringify(sortedCefEntities)
                                    )
                                );

                                setIsStepFormValid(() => () => true);

                                break;
                            case CustomerType.AX2012:
                            default:
                                const sortedAxEntities = [...payload.data].sort(
                                    compareEntities
                                );

                                setEntities(sortedAxEntities);

                                dispatch(
                                    setActiveStepData(
                                        JSON.stringify(sortedAxEntities)
                                    )
                                );

                                setIsStepFormValid(() => () => true);

                                break;
                        }

                        return null;
                    }

                    return getErrorMessage(String(payload), status);

                case StepStatus.Success: {
                    setEntities(
                        (JSON.parse(
                            activeStep.data ?? ""
                        ) as DataImportEntity[]) ?? []
                    );
                }
            }
        } catch (e) {
            if (!Axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const handleEntityChanged = (entity: DataImportEntity) => {
        const items = produce(entities, (draft) => {
            const item = draft.find((i) => i.entity === entity.entity);
            if (item) {
                item.axImport = entity.axImport;
                item.cef = entity.cef;
            }
        });

        setEntities(items);

        dispatch(setActiveStepData(JSON.stringify(items)));
        dispatch(setIsUnsavedChangeAvailable(true));
    };

    useEffect(() => {
        if (enrollWorkflow?.id) {
            fetchCache().then();
        }
    }, [enrollWorkflow?.id]);

    useEffect(() => {
        if (enrollCache) {
            fetchCustomerDetails().then();
        }
    }, [enrollCache]);

    useEffect(() => {
        if (
            activeEnvironmentMoniker &&
            enrollCache?.tenantId &&
            customerDetails
        ) {
            fetchData();
        }
    }, [activeEnvironmentMoniker, enrollCache?.tenantId, customerDetails]);

    useRefreshPage(() => {
        if (activeEnvironmentMoniker && enrollCache?.tenantId) {
            fetchData();
        }
    });

    return (
        dataFetched && (
            <Container>
                <Content>
                    <Table
                        disabled={disabled}
                        originEntities={entities}
                        entities={entities}
                        isEnroll={true}
                        isD365Customer={isD365Customer ?? false}
                        disabledSettingNames={[]}
                        workflows={[]}
                        onEntityChanged={(entity) => {
                            handleEntityChanged(entity);
                        }}
                        onMarkAndTransferAll={null}
                    />
                </Content>
            </Container>
        )
    );
};

const Component = flowRight(withLoader, withError)(DataImport);

export default Component;
